import { Injectable, inject, signal } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Router } from '@angular/router';
import {
  Observable,
  catchError,
  finalize,
  map,
  of,
  shareReplay,
  tap,
} from 'rxjs';
import { environment } from '../../../environments/environment';
import { LoginRequest, LoginResponse, User } from '../models/user.model';
import { UserRole } from '../enums/user-role.enum';
import { SessionEventsService } from './session-events.service';
import { SKIP_SESSION_INVALIDATION } from '../interceptors/http-context.tokens';

type SessionStatus = 'unknown' | 'checking' | 'authenticated' | 'anonymous';

interface SessionResponse {
  data: {
    user: User;
  };
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly sessionEvents = inject(SessionEventsService);
  private readonly apiUrl = environment.apiUrl;

  private sessionStatus = signal<SessionStatus>('unknown');
  private currentUser = signal<User | null>(null);
  private sessionRequest$: Observable<boolean> | null = null;

  currentUserRole = signal<UserRole | null>(null);

  constructor() {
    localStorage.removeItem('access_token');
    sessionStorage.removeItem('access_token');
    this.sessionEvents.remoteReset$.subscribe(() => this.clearSessionState(false));
  }

  login(credentials: LoginRequest): Observable<LoginResponse> {
    return this.http
      .post<LoginResponse>(`${this.apiUrl}/auth/login`, credentials, {
        withCredentials: true,
      })
      .pipe(tap((response) => this.setSession(response.data.user)));
  }

  ensureSession(): Observable<boolean> {
    if (this.sessionStatus() === 'authenticated') return of(true);
    if (this.sessionRequest$) return this.sessionRequest$;

    this.sessionStatus.set('checking');
    this.sessionRequest$ = this.http
      .get<SessionResponse>(`${this.apiUrl}/auth/me`, {
        withCredentials: true,
        context: new HttpContext().set(SKIP_SESSION_INVALIDATION, true),
      })
      .pipe(
        tap((response) => this.setSession(response.data.user)),
        map(() => true),
        catchError(() => {
          this.clearSessionState();
          return of(false);
        }),
        finalize(() => {
          this.sessionRequest$ = null;
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
      );

    return this.sessionRequest$;
  }

  logout(): void {
    this.clearSessionState();
    this.revokeCookie();
    void this.router.navigate(['/auth/login']);
  }

  invalidateSession(): void {
    const returnUrl = this.router.url;
    this.clearSessionState();
    this.revokeCookie();

    if (!returnUrl.startsWith('/auth/login')) {
      void this.router.navigate(['/auth/login'], { queryParams: { returnUrl } });
    }
  }

  isAuthenticated(): boolean {
    return this.sessionStatus() === 'authenticated';
  }

  getUserRole(): UserRole | null {
    return this.currentUserRole();
  }

  hasRole(...roles: Array<UserRole | string>): boolean {
    const role = this.currentUserRole();
    if (role === null) return false;
    return roles.map(String).includes(String(role));
  }

  /**
   * Vista dual del expediente: el AGENT recibe el flujo secuencial (Wizard);
   * ADMIN, ACCOUNTING y EMISSIONS reciben la vista panorámica por tabs.
   */
  isAgentView(): boolean {
    return this.currentUserRole() === UserRole.AGENT;
  }

  getUserId(): string | null {
    return this.currentUser()?.id ?? null;
  }

  getUserEmail(): string | null {
    return this.currentUser()?.email ?? null;
  }

  private setSession(user: User): void {
    this.currentUser.set(user);
    this.currentUserRole.set(user.role);
    this.sessionStatus.set('authenticated');
  }

  private clearSessionState(broadcast = true): void {
    localStorage.removeItem('access_token');
    sessionStorage.removeItem('access_token');
    this.currentUser.set(null);
    this.currentUserRole.set(null);
    this.sessionStatus.set('anonymous');
    if (broadcast) this.sessionEvents.notifyReset();
  }

  private revokeCookie(): void {
    const context = new HttpContext().set(SKIP_SESSION_INVALIDATION, true);
    this.http
      .post(`${this.apiUrl}/auth/logout`, {}, { withCredentials: true, context })
      .pipe(catchError(() => of(null)))
      .subscribe();
  }
}
